import { navigationRef } from './navigationService';

/**
 * The url the backend puts on the donation and private-message notifications (inbox and push):
 * `/chat/<playerId>` opens the private chat with that player.
 */
export const CHAT_LINK_PREFIX = '/chat/';

export const isChatLink = (url: unknown): url is string =>
	typeof url === 'string' && url.startsWith(CHAT_LINK_PREFIX);

/** The other player's id from a `/chat/<playerId>` url, or null for any other url. */
export const chatPlayerIdFromUrl = (url: unknown): string | null => {
	if (!isChatLink(url)) return null;
	const [id] = url.split('?')[0].slice(CHAT_LINK_PREFIX.length).split('/');
	if (!id) return null;
	try {
		return decodeURIComponent(id);
	} catch {
		return id;
	}
};

/** How long a push tap on a cold start waits for the navigator before giving up. */
const NAVIGATOR_WAIT_MS = 15000;
const NAVIGATOR_POLL_MS = 250;

/**
 * Opens the private chat from outside the navigator - a toast or a push tap. A push opened the
 * app from cold, so the navigator may not be mounted yet; it is waited for briefly.
 */
export function openPrivateChat(playerId: string, username?: string) {
	const startedAt = Date.now();

	const attempt = () => {
		if (!navigationRef.isReady()) {
			if (Date.now() - startedAt < NAVIGATOR_WAIT_MS) setTimeout(attempt, NAVIGATOR_POLL_MS);
			return;
		}

		const current = navigationRef.getCurrentRoute();
		const currentPlayerId = (current?.params as { playerId?: string } | undefined)?.playerId;
		if (current?.name === 'PrivateChat' && currentPlayerId === playerId) return;

		navigationRef.navigate('PrivateChat', { playerId, username });
	};

	attempt();
}

/** Routes a notification url when it is a chat link; returns whether it was one. */
export function openChatLink(url: unknown): boolean {
	const playerId = chatPlayerIdFromUrl(url);
	if (!playerId) return false;
	openPrivateChat(playerId);
	return true;
}
